"use client"

import { useEffect, useState, useCallback } from "react"
import { Wishlist, WishlistItem } from "@lib/data/wishlist"

const WISHLIST_UPDATED_EVENT = "wishlist-updated"

export function dispatchWishlistUpdate() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(WISHLIST_UPDATED_EVENT))
  }
}

export function useWishlist() {
  const [wishlist, setWishlist] = useState<Wishlist | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchWishlist = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch("/api/wishlist", { credentials: "include" })

      if (response.status === 401 || response.status === 404) {
        setWishlist(null)
        return
      }

      if (!response.ok) {
        throw new Error("Failed to fetch wishlist")
      }

      const data = await response.json()
      setWishlist(data.wishlist || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wishlist")
      setWishlist(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchWishlist()

    const handleUpdate = () => fetchWishlist()
    window.addEventListener(WISHLIST_UPDATED_EVENT, handleUpdate)
    return () => {
      window.removeEventListener(WISHLIST_UPDATED_EVENT, handleUpdate)
    }
  }, [fetchWishlist])

  const isInWishlist = useCallback(
    (variantId: string): boolean => {
      if (!wishlist) return false
      return wishlist.items.some((item) => item.product_variant_id === variantId)
    },
    [wishlist]
  )

  const getWishlistItemId = useCallback(
    (variantId: string): string | null => {
      if (!wishlist) return null
      return wishlist.items.find((item) => item.product_variant_id === variantId)?.id ?? null
    },
    [wishlist]
  )

  const addItem = async (variantId: string): Promise<boolean> => {
    try {
      const response = await fetch("/api/wishlist/items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ variant_id: variantId }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Failed to add to wishlist")
      }

      const data = await response.json()
      setWishlist(data.wishlist)
      dispatchWishlistUpdate()
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add to wishlist")
      return false
    }
  }

  const removeItem = async (itemId: string): Promise<boolean> => {
    try {
      const response = await fetch(`/api/wishlist/items/${itemId}`, {
        method: "DELETE",
        credentials: "include",
      })

      if (!response.ok) {
        throw new Error("Failed to remove from wishlist")
      }

      const data = await response.json()
      setWishlist(data.wishlist)
      dispatchWishlistUpdate()
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove from wishlist")
      return false
    }
  }

  const toggleItem = async (variantId: string): Promise<boolean> => {
    const itemId = getWishlistItemId(variantId)
    if (itemId) {
      return removeItem(itemId)
    }
    return addItem(variantId)
  }

  return {
    wishlist,
    isLoading,
    error,
    isInWishlist,
    getWishlistItemId,
    addItem,
    removeItem,
    toggleItem,
    refetch: fetchWishlist,
  }
}
